import ApiService from '@/core/services/ApiService'
import {endpoints} from '@/core/services/Endpoints'
import panelManager from '@/core/helpers/panelManager'
import eventBus from '@/core/plugins/event-bus'
import i18n from '@/core/plugins/vue-i18n'
import fileManagerService from './fileManagerService'

let _callbacks = null

function _fireEvent(name, data) {
    if (_callbacks && typeof _callbacks[name] === 'function') {
        _callbacks[name](data)
    }
}

function open(options = {}) {
    const {
        uploadableType,
        uploadableId,
        accept,
        multiple = true,
        canDelete = false,
        canDetach = false,
        onUpload,
        onDetach,
        onDelete,
        onAttach,
        onClose,
    } = options

    _callbacks = {onUpload, onDetach, onDelete, onAttach, onClose}

    panelManager.openPanel('fileManagerPanel', {
        pickerMode: true,
        accept,
        multiple,
        uploadable_type: uploadableType,
        uploadable_id: uploadableId,
        canDelete,
        canDetach,
        _pickerCallbacks: _callbacks,
    })
}

async function getFiles(uploadableType, uploadableId, options = {}) {
    const {filter, page = 1, pageLimit = 30} = options
    const params = {
        uploadable_type: uploadableType,
        uploadable_id: uploadableId,
        page,
        page_limit: pageLimit,
    }
    const res = await ApiService.axios.get(endpoints.upload, {params})
    const payload = res.data
    let files = (payload?.data || []).map(fileManagerService.mapApiFile)

    if (filter) {
        files = files.filter(f => fileManagerService.matchesAccept(f, filter))
    }

    const meta = payload?.meta || {}
    return {
        files,
        total: meta.total_record ?? files.length,
        page,
        pageLimit,
        hasMore: meta.has_more_pages ?? false,
    }
}

async function getFileCount(uploadableType, uploadableId) {
    const res = await ApiService.axios.get(endpoints.upload, {
        params: {
            uploadable_type: uploadableType,
            uploadable_id: uploadableId,
            page: 1,
            page_limit: 1,
        },
    })
    return res.data?.meta?.total_record ?? 0
}

async function attachFiles(uploadableType, uploadableId, uploadIds) {
    await ApiService.axios.post(endpoints.upload_attach_batch, {
        uploadable_type: uploadableType,
        uploadable_id: uploadableId,
        upload_ids: Array.isArray(uploadIds) ? uploadIds : [uploadIds],
    })
}

function detachFiles(uploadableType, uploadableId, uploadIds, options = {}) {
    const { confirm = true } = options
    const ids = Array.isArray(uploadIds) ? uploadIds : [uploadIds]

    const doDetach = () => ApiService.axios.post(endpoints.upload_detach_batch, {
        uploadable_type: uploadableType,
        uploadable_id: uploadableId,
        upload_ids: ids,
    })

    if (!confirm) return doDetach()

    const t = i18n.global.t
    return new Promise((resolve, reject) => {
        eventBus.$emit('showConfirmDialog', {
            title: t('common.fileManager.detachConfirmTitle'),
            message: ids.length === 1
                ? t('common.fileManager.detachConfirmSingle')
                : t('common.fileManager.detachConfirmMultiple', { count: ids.length }),
            confirmText: t('common.fileManager.detach'),
            cancelText: t('common.fileManager.cancel'),
            confirmType: 'danger',
            onConfirm: () => doDetach().then(resolve).catch(reject),
            onCancel: () => reject(new Error('cancelled')),
        })
    })
}

function getCallbacks() {
    return _callbacks
}

function clearCallbacks() {
    _callbacks = null
}

export default {
    open,
    getFiles,
    getFileCount,
    attachFiles,
    detachFiles,
    getCallbacks,
    clearCallbacks,
    _fireEvent,
}
